import { find, get, isArray, isNumber, pickBy } from 'lodash';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useGridSettings } from 'components/SearchResultsMUI/useGridSettings';
import { useGrid } from 'utils/hooks/useGrid';
import {
  DATAGRID_SETTINGS_COLUMN_ORDER_MODEL,
  DATAGRID_SETTINGS_COLUMN_SORT_MODEL,
  DATAGRID_SETTINGS_COLUMN_WIDTHS,
} from 'constants/constants';

export const useMuiGrid = (gridKey: string) => {
  const {
    gridSettings,
    getUpdatedColumnWidthsAfterResize,
    getGridColumnsSort,
    getUpdatedColumnOrderAfterReordering,
    getGridColumnsWidths,
    getGridColumnsOrder,
    getGridColumnsVisibility,
    storeGridSettings,
  } = useGridSettings();

  const columnWidths = getGridColumnsWidths(gridKey);
  const columnOrder = getGridColumnsOrder(gridKey);
  const gridSortModel = getGridColumnsSort(gridKey);
  const columnVisibilityModel = getGridColumnsVisibility(gridKey);

  const { setGridSettingsAndStore } = useGrid();

  const reorderColumnsByGridSettings = (columns: any[]) => {
    if (!isArray(columnOrder) || columnOrder.length === 0) {
      return columns;
    }
    const sortedColumns = [...columns].sort((a, b) => {
      const indexA = columnOrder.indexOf(a.field);
      const indexB = columnOrder.indexOf(b.field);

      // Pokud a ani b nejsou v order poli, ponecháme je ve stávajícím pořadí.
      if (indexA === -1 && indexB === -1) {
        return 0;
      }

      // Pokud a není v order poli, posuneme ho za b.
      if (indexA === -1) {
        return 1;
      }

      // Pokud b není v order poli, posuneme ho za a.
      if (indexB === -1) {
        return -1;
      }

      // Oba prvky jsou v order poli, vrátíme je podle jejich pořadí v order poli.
      return indexA - indexB;
    });

    return sortedColumns;
  };

  const injectColumnWidthsIntoColumns = (columns: any, defaultWidth: number = 150) =>
    columns.map((column: any) => {
      const columnWidthFromGridSettings = get(find(columnWidths, { field: column.field }), 'width');
      return {
        ...column,
        width: isNumber(columnWidthFromGridSettings)
          ? columnWidthFromGridSettings
          : isNumber(column.width)
            ? column.width
            : defaultWidth,
      };
    });

  const getGridSettingsForApi = () => {
    return {
      ...get(gridSettings, gridKey, {}),
    };
  };

  const setAndStore = (gridSettingsForAPI: any, storeToDb: boolean = true) => {
    const gridSettingsForRedux = {
      ...gridSettings,
      [gridKey]: gridSettingsForAPI,
    };

    setGridSettingsAndStore(
      {
        key: gridKey,
        gridSettingsForAPI,
        gridSettingsForRedux,
      },
      storeToDb,
    );
  };

  const changeWidthColumn = (columns: any[], changedColumn: GridColumnResizeParams) => {
    if (!changedColumn?.colDef?.field) {
      return;
    }

    const columnNewWidth = changedColumn.width;
    const columnName = changedColumn.colDef.field;

    const gridSettingsForAPI = {
      ...get(gridSettings, gridKey, {}),
      [DATAGRID_SETTINGS_COLUMN_WIDTHS]: getUpdatedColumnWidthsAfterResize(
        columns,
        columnName,
        columnNewWidth,
      ),
    };
    setAndStore(gridSettingsForAPI);
  };

  const updateColumnOrder = (
    columns: any[],
    params: GridColumnOrderChangeParams,
    indexShiftForReordering: number = 0,
  ) => {
    if (!params?.column?.field) {
      return;
    }

    const newColumnOrder = getUpdatedColumnOrderAfterReordering(
      columns,
      params.column.field,
      params.oldIndex - indexShiftForReordering,
      params.targetIndex - indexShiftForReordering,
    );
    const gridSettingsForAPI = {
      ...get(gridSettings, gridKey, {}),
      [DATAGRID_SETTINGS_COLUMN_ORDER_MODEL]: newColumnOrder.map((column) => column.field),
    };

    setAndStore(gridSettingsForAPI);
  };

  const sortByColumn = (sortModel: GridSortModel) => {
    const gridSettingsForAPI = {
      ...get(gridSettings, gridKey, {}),
      [DATAGRID_SETTINGS_COLUMN_SORT_MODEL]: sortModel,
    };

    setAndStore(gridSettingsForAPI);
  };

  const setColumnVisibilityModel = (newModel: GridColumnVisibilityModel) => {
    storeGridSettings(gridKey, {
      // ukládáme pouze sloupce, které jsou false (= schované)
      columnVisibilityModel: pickBy(newModel, (item: Boolean) => item === false),
    });
  };

  const hiddenFields = ['__detail_panel_toggle__', '__check__', 'actions'];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  return {
    columnVisibilityModel,
    gridSortModel,
    injectColumnWidthsIntoColumns,
    reorderColumnsByGridSettings,
    setColumnVisibilityModel,
    changeWidthColumn,
    updateColumnOrder,
    sortByColumn,
    getTogglableColumns,
    setAndStore,
    getGridSettingsForApi,
  };
};
